import { Link, createFileRoute } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'
import { useEffect } from 'react'
import { Button } from '@/components/ui/button'

export const Route = createFileRoute('/terms')({
  component: RouteComponent,
})

function RouteComponent() {
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-12 max-w-3xl">
        <Link to="/">
          <Button
            variant="ghost"
            className="mb-8 text-frost/70 hover:text-frost"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Home
          </Button>
        </Link>

        <article className="prose prose-invert prose-lg max-w-none">
          <h1 className="text-4xl font-bold text-frost mb-2">
            Terms & Conditions
          </h1>
          <p className="text-frost/50 text-sm mb-8">
            Last updated: December 20, 2025
          </p>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-frost mb-4">
              1. Acceptance of Terms
            </h2>
            <p className="text-frost/70">
              By accessing or using Snow Spot ("Service"), you agree to be bound
              by these Terms & Conditions. If you do not agree, do not use the
              Service.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-frost mb-4">
              2. Description of Service
            </h2>
            <p className="text-frost/70">
              Snow Spot provides snow forecasts, SNOTEL data, interactive maps,
              alerts, and related tools for recreational planning. Information
              provided is for informational purposes only and should not be
              relied upon as a guarantee of conditions or safety.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-frost mb-4">
              3. User Accounts
            </h2>
            <p className="text-frost/70">You are responsible for:</p>
            <ul className="text-frost/70 list-disc pl-6">
              <li>
                Maintaining the confidentiality of your account credentials
              </li>
              <li>All activities that occur under your account</li>
              <li>Notifying us immediately of any unauthorized use</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-frost mb-4">
              4. Subscriptions & Payments
            </h2>
            <p className="text-frost/70 mb-2">
              Some features require a paid subscription.
            </p>
            <ul className="text-frost/70 list-disc pl-6">
              <li>
                Subscriptions are billed on a recurring basis (monthly or
                annually)
              </li>
              <li>
                Payments are processed by third-party providers (e.g., Stripe,
                Apple, Google)
              </li>
              <li>Prices are subject to change with notice</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-frost mb-4">
              5. Cancellations & Refunds
            </h2>
            <p className="text-frost/70 mb-2">
              You may cancel your subscription at any time.
            </p>
            <ul className="text-frost/70 list-disc pl-6">
              <li>Access remains active until the end of the billing period</li>
              <li>No refunds are provided for partial billing periods</li>
              <li>
                Lifetime or promotional plans are non-refundable unless required
                by law
              </li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-frost mb-4">
              6. Acceptable Use
            </h2>
            <p className="text-frost/70 mb-2">You agree not to:</p>
            <ul className="text-frost/70 list-disc pl-6">
              <li>Use the Service for unlawful purposes</li>
              <li>Attempt to reverse engineer or scrape the platform</li>
              <li>Interfere with or disrupt the Service</li>
              <li>Violate the rights of other users</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-frost mb-4">
              7. Assumption of Risk
            </h2>
            <p className="text-frost/70 mb-2">Snow Spot provides data only.</p>
            <p className="text-frost/70 mb-2">You acknowledge that:</p>
            <ul className="text-frost/70 list-disc pl-6 mb-4">
              <li>Weather and snow conditions can change rapidly</li>
              <li>Outdoor activities involve inherent risk</li>
              <li>You are solely responsible for decisions and safety</li>
            </ul>
            <p className="text-frost/70">
              Snow Spot is not liable for injuries, losses, or damages arising
              from use of the Service.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-frost mb-4">
              8. Intellectual Property
            </h2>
            <p className="text-frost/70">
              All content, trademarks, logos, and software are owned by Snow
              Spot, LLC or its licensors. You may not copy, distribute, or
              modify any part of the Service without written permission.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-frost mb-4">
              9. Termination
            </h2>
            <p className="text-frost/70">
              We reserve the right to suspend or terminate access to the Service
              at our discretion, including for violations of these Terms.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-frost mb-4">
              10. Disclaimer of Warranties
            </h2>
            <p className="text-frost/70">
              The Service is provided "as is" and "as available." We make no
              warranties regarding accuracy, reliability, or availability.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-frost mb-4">
              11. Limitation of Liability
            </h2>
            <p className="text-frost/70">
              To the maximum extent permitted by law, Snow Spot shall not be
              liable for indirect, incidental, or consequential damages arising
              from use of the Service.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-frost mb-4">
              12. Governing Law
            </h2>
            <p className="text-frost/70">
              These Terms are governed by the laws of the State of Montana,
              without regard to conflict of law principles.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-frost mb-4">
              13. Changes to Terms
            </h2>
            <p className="text-frost/70">
              We may update these Terms from time to time. Continued use of the
              Service constitutes acceptance of updated terms.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-frost mb-4">
              14. Contact Information
            </h2>
            <p className="text-frost/70">
              For questions about these Terms, contact:{' '}
              <a
                href="mailto:info@snowspot.app"
                className="text-ice hover:underline"
              >
                info@snowspot.app
              </a>
            </p>
          </section>
        </article>
      </div>
    </div>
  )
}
